import { constants } from "@qgiv/core-js";
import { useMemo } from "react";
import { getCardToken, getUrlOfPaymentIframe } from "@qgiv/core-react";
import { useDispatch, useSelector } from "react-redux";
import {
    getPaymentTypeAllowsRecurringUpgrade,
    getShouldDisplayRecurringUpgrade,
} from "@qgiv/donation-form";
import {
    selectConfig,
    selectIsDisplayTypeModal,
} from "../../redux/slices/configSettingsSlice";
import {
    selectGiftDetails,
    selectSubmitDonation,
    selectValidateOnePayDonation,
} from "../../redux/slices/donationDetailsSlice";
import {
    selectAllFormSettings,
    selectHasOnePay,
    selectPaymentData,
} from "../../redux/slices/formSettingsSlice";
import {
    selectDonorAccount,
    selectIsLoggedIn,
} from "../../redux/slices/donorAccountSlice";
import {
    selectAddressGroupIsDisplayable,
    selectDefaultCountry,
    selectDisplayableBillingFields,
    selectHasBillingNameField,
} from "../../redux/slices/fieldsSlice";
import { selectAllDonationSettings } from "../../redux/slices/donationSettingsSlice";
import {
    selectContentToDisplay,
    setErrors,
    setShouldScrollToTop,
    updateContentToDisplay,
} from "../../redux/slices/appSettingsSlice";
import { ModalContentReplacerEnums } from "../common/ModalContentReplacer";
import { selectHasActiveFulfillment } from "../../redux/slices/promiseTransactionSlice";
import { buildPaymentPageFormikSettings } from "./pageDataHelper";
import { scrollTop } from "../PostMessage/postMessage";
import { selectAcceptedEmailReminders } from "../../redux/slices/abandonedGiftSlice";

const usePaymentPageData = () => {
    const {
        ENUMS: {
            EntityType: {
                SYSTEM_FIELD_GROUP_BILLING_ADDRESS,
                SYSTEM_FIELD_GROUP_MAILING_ADDRESS,
            },
            PaymentType,
        },
    } = constants;

    const dispatch = useDispatch();

    const submitDonation = useSelector(selectSubmitDonation);

    const paymentData = useSelector(selectPaymentData);
    const config = useSelector(selectConfig);
    const donorAccount = useSelector(selectDonorAccount);
    const formSettings = useSelector(selectAllFormSettings);
    const isLoggedIn = useSelector(selectIsLoggedIn);
    const giftDetails = useSelector(selectGiftDetails);
    const { QGIV_HOST, isCms } = config;
    const { captcha, enableDonorLogins, enableRecur, id, VisaAmexDisc } =
        formSettings;
    const { hasSelectedRecurringDonation, total, subtotal } = giftDetails;
    const { enableCCPayments } = paymentData;

    const displayableBillingFields = useSelector(
        selectDisplayableBillingFields,
    );

    const displayNameFieldData = useSelector(selectHasBillingNameField);
    const billingDefaultCountry = useSelector((state) =>
        selectDefaultCountry(state, SYSTEM_FIELD_GROUP_BILLING_ADDRESS),
    );

    const mailingAddressFieldsData = useSelector((state) =>
        selectAddressGroupIsDisplayable(
            state,
            SYSTEM_FIELD_GROUP_MAILING_ADDRESS,
        ),
    );

    const donationSettings = useSelector(selectAllDonationSettings);
    const {
        enableAbandonedGifts = false,
        enableReminderEmails = false,
        enableRecurringCtaLimit = false,
        recurringCtaAfter = false,
        recurringCtaLimitAmount = 0,
    } = donationSettings;

    const hasActiveFulfillment = useSelector(selectHasActiveFulfillment);
    const acceptedEmailReminders = useSelector(selectAcceptedEmailReminders);

    const isDisplayTypeModal = useSelector(selectIsDisplayTypeModal);
    const hasOnePay = useSelector(selectHasOnePay);
    const validateOnePayDonation = useSelector(selectValidateOnePayDonation);

    const selectedContentToDisplay = useSelector(selectContentToDisplay);

    const shouldDisplayRecurringUpgrade = getShouldDisplayRecurringUpgrade({
        enableRecur,
        enableRecurringCtaLimit,
        hasActiveFulfillment,
        hasSelectedRecurringDonation,
        recurringCtaAfter,
        recurringCtaLimitAmount,
        subtotal,
    });

    const formId = id;
    const originOfPaymentIframe = QGIV_HOST;
    const urlOfPaymentIframe = getUrlOfPaymentIframe({
        formId,
        isCms,
        originOfPaymentIframe,
    });

    const paymentPageData = useMemo(() => {
        // Abort the attempt to submit a donation if one of the fields in the
        // payment iframe throws a validation error, an unexpected error occurs
        // when attempting to generate a new card token or the recurring
        // upgrade modal needs to be displayed.
        const getShouldSubmitDonation = (values, formikBag) => {
            const { setFieldValue, setSubmitting } = formikBag;
            const { Payment_Type } = values;
            const requiresCardTokenFromPaymentIframe =
                Payment_Type === PaymentType.CREDITCARD;
            const paymentTypeAllowsRecurringUpgrade =
                getPaymentTypeAllowsRecurringUpgrade(Payment_Type);

            const displayRecurringUpgradeModal = () => {
                if (isDisplayTypeModal) {
                    dispatch(setShouldScrollToTop(true));
                } else {
                    scrollTop();
                }
                setTimeout(() => {
                    dispatch(
                        updateContentToDisplay(
                            ModalContentReplacerEnums.UPGRADE_RECURRING_UI,
                        ),
                    );
                }, 50);
            };

            const shouldSubmitDonation = new Promise((resolve, reject) => {
                const handleCardTokenSuccess = (response) => {
                    const { validationErrors, token } = response;
                    const hasPaymentIframeValidationErrors =
                        Object.keys(validationErrors).length > 0;

                    // Updating Formik gives the submit logic access to the new
                    // token however Credit Card fields are only present when
                    // credit cards are enabled on the form.
                    if (enableCCPayments) {
                        setFieldValue("Card_Token", token);
                    }

                    // Only stop the submission animation if we have
                    // validation errors as the recurring upgrade modal and
                    // submission logic will reset it as needed after this point
                    if (hasPaymentIframeValidationErrors) {
                        setSubmitting(false);
                        resolve({
                            shouldSubmitDonation: false,
                        });
                    } else if (
                        !hasPaymentIframeValidationErrors &&
                        shouldDisplayRecurringUpgrade &&
                        paymentTypeAllowsRecurringUpgrade
                    ) {
                        displayRecurringUpgradeModal();
                        resolve({
                            shouldSubmitDonation: false,
                        });
                    } else {
                        resolve({
                            shouldSubmitDonation: true,
                        });
                    }
                };

                const handleCardTokenError = (response) => {
                    const { errors } = response;
                    dispatch(setErrors(errors));
                    setSubmitting(false);
                    reject(response);
                };

                getCardToken(
                    urlOfPaymentIframe,
                    requiresCardTokenFromPaymentIframe,
                )
                    .then(handleCardTokenSuccess)
                    .catch(handleCardTokenError);
            });

            return shouldSubmitDonation;
        };

        const formikSettings = buildPaymentPageFormikSettings({
            billingDefaultCountry,
            captcha,
            displayableBillingFields,
            displayNameFieldData,
            donorAccount,
            enableDonorLogins,
            hasSelectedRecurringDonation,
            isLoggedIn,
            mailingAddressFieldsData,
            paymentData,
            total,
            VisaAmexDisc,
        });

        return {
            acceptedEmailReminders,
            documentTitle: "Payment",
            enableAbandonedGifts,
            enableReminderEmails,
            formikSettings,
            getShouldSubmitDonation,
            hasOnePay,
            selectedContentToDisplay,
            shouldDisplayRecurringUpgrade,
            submitDonation,
            validateOnePayDonation,
        };
    }, [
        VisaAmexDisc,
        acceptedEmailReminders,
        billingDefaultCountry,
        captcha,
        dispatch,
        displayNameFieldData,
        displayableBillingFields,
        donorAccount,
        enableCCPayments,
        enableAbandonedGifts,
        enableReminderEmails,
        enableDonorLogins,
        hasSelectedRecurringDonation,
        isDisplayTypeModal,
        isLoggedIn,
        hasOnePay,
        mailingAddressFieldsData,
        paymentData,
        PaymentType.CREDITCARD,
        selectedContentToDisplay,
        shouldDisplayRecurringUpgrade,
        submitDonation,
        total,
        urlOfPaymentIframe,
        validateOnePayDonation,
    ]);

    return paymentPageData;
};

export default usePaymentPageData;
